import { useEffect, useState, useMemo } from 'react';
import { leaveAPI, employeesAPI, departmentsAPI, disciplinaryAPI } from '../lib/api';
import { formatDate, getStatusColor, getLeaveTypeColor, cn } from '../lib/utils';
import { Clock, Plus, Calendar as CalendarIcon, Check, X, Filter, AlertTriangle, RefreshCw, History, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { LeaveModals } from '../components/leave/LeaveModals';

const leaveTypes = ['Annual Leave', 'Sick Leave', 'Family Responsibility Leave', 'Unpaid Leave', 'Public Holiday Leave', 'Other'];

export default function Leave() {
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  // Edit-leave (correction) state
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editRequest, setEditRequest] = useState({
    id: '', employee_id: '', leave_type: '', start_date: '', end_date: '', reason: '',
  });
  // Disciplinary prompt state — triggered when approving an Unpaid Leave
  // pushes the balance below zero.
  const [discPrompt, setDiscPrompt] = useState(null);
  // Unpaid-leave confirmation state — triggered when approving unpaid leave
  // would take the balance below -3 days. No hard block; admin confirms.
  const [unpaidConfirm, setUnpaidConfirm] = useState(null);
  const [confirmingUnpaid, setConfirmingUnpaid] = useState(false);
  const [creatingCase, setCreatingCase] = useState(false);
  const [caseForm, setCaseForm] = useState({ case_type: 'Verbal Warning', description: '' });
  const [newRequest, setNewRequest] = useState({
    employee_id: '',
    leave_type: '',
    start_date: '',
    end_date: '',
    reason: '',
  });
  const [runningAccrual, setRunningAccrual] = useState(false);  const [accruals, setAccruals] = useState([]);
  const [accrualsLoading, setAccrualsLoading] = useState(false);
  const { isHRManager } = useAuth();

  // Memoised view of upcoming approved leave (top 5). Recomputed only when
  // `requests` changes to avoid the filter running on every render.
  const upcomingApprovedLeave = useMemo(() => {
    const now = new Date();
    return requests
      .filter(r => r.status === 'Approved' && new Date(r.start_date) >= now)
      .slice(0, 5);
  }, [requests]);
  const hasUpcomingApprovedLeave = upcomingApprovedLeave.length > 0;

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterType]);

  const fetchData = async () => {
    try {
      const [requestsRes, empRes, deptRes] = await Promise.all([
        leaveAPI.getAll({
          status: filterStatus !== 'all' ? filterStatus : undefined,
          leave_type: filterType !== 'all' ? filterType : undefined,
        }),
        employeesAPI.getAll({ status: 'Active' }),
        departmentsAPI.getAll(),
      ]);
      setRequests(requestsRes.data);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!newRequest.employee_id || !newRequest.leave_type || !newRequest.start_date || !newRequest.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await leaveAPI.create(newRequest);
      toast.success('Leave request created');
      setShowNewRequestModal(false);
      setNewRequest({
        employee_id: '',
        leave_type: '',
        start_date: '',
        end_date: '',
        reason: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    }
  };

  const handleApprove = async (id, adminOverride = false) => {
    try {
      const payload = adminOverride
        ? { status: 'Approved', admin_override: true }
        : { status: 'Approved' };
      const res = await leaveAPI.update(id, payload);
      // No hard block: backend asks the admin to confirm when unpaid leave
      // would drop below -3 days.
      if (res?.data?.requires_confirmation) {
        setUnpaidConfirm({ id, warning: res.data.warning });
        return;
      }
      toast.success('Leave approved');
      // If the backend flags that the approval took the employee's balance
      // below zero (Unpaid Leave -> misconduct), open a disciplinary prompt.
      const prompt = res?.data?.disciplinary_prompt;
      if (prompt?.should_open_case) {
        setCaseForm({
          case_type: 'Verbal Warning',
          description: `Unpaid leave approved for ${prompt.balance_before - prompt.balance_after} day(s) beyond available balance. Balance is now ${prompt.balance_after} days. Misconduct review required.`,
        });
        setDiscPrompt(prompt);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve leave');
    }
  };

  const handleConfirmUnpaid = async () => {
    if (!unpaidConfirm) return;
    setConfirmingUnpaid(true);
    const { id } = unpaidConfirm;
    setUnpaidConfirm(null);
    await handleApprove(id, true);
    setConfirmingUnpaid(false);
  };

  const handleCreateDisciplinaryCase = async () => {
    if (!discPrompt) return;
    if (!caseForm.case_type || !caseForm.description.trim()) {
      toast.error('Case type and description are required');
      return;
    }
    setCreatingCase(true);
    try {
      await disciplinaryAPI.create({
        employee_id: discPrompt.employee_id,
        case_type: caseForm.case_type,
        description: caseForm.description,
        date: new Date().toISOString().slice(0, 10),
        outcome: '',
        related_leave_id: discPrompt.leave_id,
      });
      toast.success(`Disciplinary case opened for ${discPrompt.employee_name}`);
      setDiscPrompt(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create disciplinary case');
    } finally {
      setCreatingCase(false);
    }
  };

  const handleReject = async (id) => {
    try {
      await leaveAPI.update(id, { status: 'Rejected' });
      toast.success('Leave rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  const openEdit = (request) => {
    setEditRequest({
      id: request.id,
      employee_id: request.employee_id,
      leave_type: request.leave_type,
      start_date: request.start_date,
      end_date: request.end_date,
      reason: request.reason || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editRequest.employee_id || !editRequest.leave_type || !editRequest.start_date || !editRequest.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (editRequest.end_date < editRequest.start_date) {
      toast.error('End date cannot be before start date');
      return;
    }
    setSavingEdit(true);
    try {
      await leaveAPI.edit(editRequest.id, {
        employee_id: editRequest.employee_id,
        leave_type: editRequest.leave_type,
        start_date: editRequest.start_date,
        end_date: editRequest.end_date,
        reason: editRequest.reason,
      });
      toast.success('Leave request updated');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update leave request');
    } finally {
      setSavingEdit(false);
    }
  };

  const fetchAccruals = async () => {
    setAccrualsLoading(true);
    try {
      const res = await leaveAPI.getAccruals({ limit: 100 });
      setAccruals(res.data || []);
    } catch (error) {
      toast.error('Failed to load accrual history');
    } finally {
      setAccrualsLoading(false);
    }
  };

  const handleRunAccrual = async () => {
    setRunningAccrual(true);
    try {
      const res = await leaveAPI.runAccrual();
      const { scanned, employees_updated } = res.data || {};
      if (employees_updated > 0) {
        toast.success(`Accrual run complete — ${employees_updated} of ${scanned} employees updated`);
      } else {
        toast.info(`Accrual run complete — no updates due (${scanned} employees scanned)`);
      }
      fetchAccruals();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run accrual');
    } finally {
      setRunningAccrual(false);
    }
  };

  // Get leave dates for calendar
  const leaveDates = requests
    .filter(r => r.status === 'Approved')
    .flatMap(r => {
      const dates = [];
      const start = new Date(r.start_date);
      const end = new Date(r.end_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d));
      }
      return dates;
    });

  return (
    <div className="space-y-6 page-transition" data-testid="leave-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
            <Clock className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            Leave & Absence
          </h1>
          <p className="text-muted-foreground mt-1">Manage employee leave requests</p>
        </div>
        {isHRManager() && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRunAccrual}
              disabled={runningAccrual}
              data-testid="run-accrual-btn"
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", runningAccrual && "animate-spin")} />
              {runningAccrual ? 'Running…' : 'Run Accrual Now'}
            </Button>
            <Button onClick={() => setShowNewRequestModal(true)} data-testid="new-leave-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="list" className="space-y-6" onValueChange={(v) => { if (v === 'accruals') fetchAccruals(); }}>
        <TabsList className="bg-secondary/30 w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="list">Requests List</TabsTrigger>
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          {isHRManager() && <TabsTrigger value="accruals" data-testid="tab-accruals">Accrual History</TabsTrigger>}
        </TabsList>

        {/* List View */}
        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <div className="glass-card p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-leave-status">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-leave-type">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {leaveTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-3">
            {loading ? (
              <div className="glass-card p-6 text-center text-muted-foreground">Loading requests...</div>
            ) : requests.length === 0 ? (
              <div className="glass-card p-6 text-center text-muted-foreground">No leave requests found</div>
            ) : (
              requests.map((request) => (
                <div key={request.id} className="glass-card p-4 space-y-3" data-testid={`leave-card-${request.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{request.employee_name}</p>
                      <p className="text-sm text-muted-foreground truncate">{request.department}</p>
                    </div>
                    <Badge variant="outline" className={cn("border", getStatusColor(request.status))}>
                      {request.status}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={cn("border text-xs", getLeaveTypeColor(request.leave_type))}>
                      {request.leave_type}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {formatDate(request.start_date)} - {formatDate(request.end_date)} ({request.days} days)
                    </span>
                  </div>
                  {isHRManager() && (
                    <div className="flex gap-2 pt-1">
                      {request.status === 'Pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                            onClick={() => handleApprove(request.id)}
                            data-testid={`approve-leave-m-${request.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-red-400 border-red-500/30 hover:bg-red-500/10"
                            onClick={() => handleReject(request.id)}
                            data-testid={`reject-leave-m-${request.id}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => openEdit(request)}
                        data-testid={`edit-leave-m-${request.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" /> Edit
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Table (tablet / desktop) */}
          <div className="hidden md:block glass-card table-scroll">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Status</TableHead>
                  {isHRManager() && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Loading requests...
                    </TableCell>
                  </TableRow>
                ) : requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No leave requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => (
                    <TableRow key={request.id} className="table-row-hover" data-testid={`leave-row-${request.id}`}>
                      <TableCell className="font-medium">{request.employee_name}</TableCell>
                      <TableCell className="text-muted-foreground">{request.department}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn("border", getLeaveTypeColor(request.leave_type))}>
                          {request.leave_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(request.start_date)} - {formatDate(request.end_date)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{request.days}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn("border", getStatusColor(request.status))}>
                          {request.status}
                        </Badge>
                      </TableCell>
                      {isHRManager() && (
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            {request.status === 'Pending' && (
                              <>
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className="text-emerald-500 hover:text-emerald-400"
                                  onClick={() => handleApprove(request.id)}
                                  data-testid={`approve-leave-${request.id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className="text-red-500 hover:text-red-400"
                                  onClick={() => handleReject(request.id)}
                                  data-testid={`reject-leave-${request.id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-muted-foreground hover:text-primary"
                              onClick={() => openEdit(request)}
                              data-testid={`edit-leave-${request.id}`}
                              title="Edit / correct this request"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Calendar View */}
        <TabsContent value="calendar">
          <div className="glass-card p-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <Calendar
                  mode="single"
                  selected={calendarDate}
                  onSelect={setCalendarDate}
                  modifiers={{ leave: leaveDates }}
                  modifiersStyles={{
                    leave: { backgroundColor: 'hsl(var(--primary))', color: 'white', borderRadius: '4px' }
                  }}
                  className="rounded-md border"
                  data-testid="leave-calendar"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-4">Upcoming Leave</h3>
                <div className="space-y-3">
                  {upcomingApprovedLeave.map((request) => (
                    <div key={request.id} className="p-3 rounded-lg bg-secondary/30">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{request.employee_name}</p>
                        <Badge variant="outline" className={cn("border text-xs", getLeaveTypeColor(request.leave_type))}>
                          {request.leave_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {formatDate(request.start_date)} - {formatDate(request.end_date)} ({request.days} days)
                      </p>
                    </div>
                  ))}
                  {!hasUpcomingApprovedLeave && (
                    <p className="text-muted-foreground">No upcoming approved leave</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Accrual History Tab */}
        {isHRManager() && (
          <TabsContent value="accruals" className="space-y-4">
            <div className="glass-card p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <History className="w-5 h-5 text-purple-400 mt-1" />
                <div>
                  <h3 className="font-semibold">Leave Accrual History</h3>
                  <p className="text-sm text-muted-foreground">
                    Annual leave accrues 1.25 days/month. Sick leave resets to 30 days every 3 years.
                    Daily automated run at 00:05 SAST.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAccruals}
                disabled={accrualsLoading}
                data-testid="refresh-accruals-btn"
              >
                <RefreshCw className={cn("w-4 h-4 mr-2", accrualsLoading && "animate-spin")} />
                Refresh
              </Button>
            </div>
            <div className="glass-card table-scroll">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Employee</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Balance After</TableHead>
                    <TableHead>Run Date</TableHead>
                    <TableHead>Trigger</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accrualsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Loading accrual history...
                      </TableCell>
                    </TableRow>
                  ) : accruals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No accrual history yet. Set accrual anchor dates on an employee and click "Run Accrual Now".
                      </TableCell>
                    </TableRow>
                  ) : (
                    accruals.map((a) => (
                      <TableRow key={a.id} data-testid={`accrual-row-${a.id}`}>
                        <TableCell className="font-medium">{a.employee_name || a.employee_id}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn(
                            "border",
                            a.leave_type === 'annual' ? 'border-indigo-500/40 text-indigo-300' : 'border-red-500/40 text-red-300'
                          )}>
                            {a.leave_type === 'annual' ? 'Annual Accrual' : 'Sick Cycle Reset'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {a.leave_type === 'annual'
                            ? `${a.months} month${a.months === 1 ? '' : 's'} × 1.25`
                            : `3-year cycle reset (new start ${a.cycle_start || '—'})`}
                        </TableCell>
                        <TableCell className="font-mono">+{a.days_accrued}</TableCell>
                        <TableCell className="font-mono">{a.balance_after}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(a.accrual_date)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {a.triggered_by === 'scheduler' ? 'Scheduler' : 'Manual'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Edit / Correct Leave Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-leave-modal">
          <DialogHeader>
            <DialogTitle>Edit Leave Request</DialogTitle>
            <DialogDescription>
              Correct the details of this leave request. If it is already approved, leave balances are automatically re-synced.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select
                value={editRequest.employee_id}
                onValueChange={(v) => setEditRequest({ ...editRequest, employee_id: v })}
              >
                <SelectTrigger data-testid="edit-leave-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.full_name} - {emp.department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Leave Type *</Label>
              <Select
                value={editRequest.leave_type}
                onValueChange={(v) => setEditRequest({ ...editRequest, leave_type: v })}
              >
                <SelectTrigger data-testid="edit-leave-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={editRequest.start_date}
                  onChange={(e) => setEditRequest({ ...editRequest, start_date: e.target.value })}
                  data-testid="edit-leave-start"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={editRequest.end_date}
                  onChange={(e) => setEditRequest({ ...editRequest, end_date: e.target.value })}
                  data-testid="edit-leave-end"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                value={editRequest.reason}
                onChange={(e) => setEditRequest({ ...editRequest, reason: e.target.value })}
                placeholder="Optional: Provide reason for leave..."
                rows={3}
                data-testid="edit-leave-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit} data-testid="save-leave-edit-btn">
              {savingEdit ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeaveModals
        showNewRequestModal={showNewRequestModal} setShowNewRequestModal={setShowNewRequestModal}
        newRequest={newRequest} setNewRequest={setNewRequest} employees={employees} handleCreateRequest={handleCreateRequest}
        discPrompt={discPrompt} setDiscPrompt={setDiscPrompt} caseForm={caseForm} setCaseForm={setCaseForm}
        creatingCase={creatingCase} handleCreateDisciplinaryCase={handleCreateDisciplinaryCase}
        unpaidConfirm={unpaidConfirm} setUnpaidConfirm={setUnpaidConfirm}
        confirmingUnpaid={confirmingUnpaid} handleConfirmUnpaid={handleConfirmUnpaid}
      />
    </div>
  );
}
